import React, { useEffect, useRef, useState } from 'react';
import { View, useWindowDimensions } from 'react-native';
import { useT } from '@transifex/react';

import { useBiometricEnrollmentPrompt } from '../../biometric/BiometricEnrollmentProvider';
import { ERROR_MESSAGES } from '../../utils/constants';
import { screenTestId } from '../../utils/testId';
import { completeAuth, handleTwoFactorLogin, resendTwoFactorCode } from '../../services/authService';
import { validate2FACode } from '../../services/validationService';
import { analytics } from '../../utils/analytics';
import { useOnAuthenticated } from '../../hooks/useOnAuthenticated';

import { BloomButton } from '../BloomButton';
import { InlineError } from '../InlineError';
import { Input } from '../Input';
import { Logo } from '../Logo';
import { TextLink } from '../TextLink';
import ThemedText from '../ThemedText';

interface TwoFactorFormProps {
  onBack: () => void;
  email: string;
  password: string;
  lastFourDigits?: string;
  nextUrl?: string;
  returnTo?: string;
}

const tid = screenTestId('TwoFactor');

export const TwoFactorForm: React.FC<TwoFactorFormProps> = ({ onBack, email, password, lastFourDigits, nextUrl, returnTo }) => {
  const { width } = useWindowDimensions();
  const isDesktop = width >= 768;
  const t = useT();
  const onAuthenticated = useOnAuthenticated();
  const { promptIfEligible } = useBiometricEnrollmentPrompt();

  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const resentTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (resentTimerRef.current) clearTimeout(resentTimerRef.current);
    };
  }, []);

  const handleSubmit = async () => {
    setError(null);

    const codeValidation = validate2FACode(code);
    if (codeValidation) {
      setError(codeValidation);
      return;
    }

    setLoading(true);
    analytics.trackEvent('2fa_attempted');

    try {
      const { token, nextApp } = await handleTwoFactorLogin(email, password, code, nextUrl);
      if (!token) {
        // handleTwoFactorLogin's success path always returns a token; this
        // guards against a server contract change so we don't silently
        // mint a Bearer-empty Zendesk SSO call.
        analytics.trackEvent('2fa_succeeded_missing_token');
        throw new Error('Missing token after two-factor verification');
      }
      analytics.trackEvent('2fa_succeeded');
      await promptIfEligible({ accessToken: token, email });
      await completeAuth({
        token,
        method: '2fa',
        urlNextUrl: nextUrl,
        urlReturnTo: returnTo,
        nextApp,
        onAuthenticated,
      });
    } catch (err: any) {
      analytics.trackEvent('2fa_failed', { error_type: 'invalid_code' });
      setError(ERROR_MESSAGES.INVALID_2FA_CODE);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResending(true);
    setError(null);

    try {
      await resendTwoFactorCode({ email, password });
      analytics.trackEvent('2fa_code_resent');
      setResent(true);
      if (resentTimerRef.current) clearTimeout(resentTimerRef.current);
      resentTimerRef.current = setTimeout(() => setResent(false), 10_000);
    } catch (err: any) {
      setError(t('Failed to resend code. Please try again.'));
    } finally {
      setResending(false);
    }
  };

  const phoneHint = lastFourDigits
    ? t('Code sent to mobile device ending in {digits}', { digits: lastFourDigits })
    : t('Code sent to mobile device');

  return (
    <View className='w-full'>
      <Logo />

      <View className='w-full'>
        <ThemedText type='titleDefault' className='mb-6 font-semibold' style={{ fontSize: 20, lineHeight: 28 }}>
          {t('Two Factor Authentication')}
        </ThemedText>

        <View className='flex-row items-center mb-6'>
          {resent ? (
            <ThemedText type='bodySmall' className='text-login-green-50'>
              {t('Code resent successfully.')}
            </ThemedText>
          ) : (
            <>
              <ThemedText type='bodySmall' color='subtle'>
                {t("Didn't get a code?")}{' '}
              </ThemedText>
              <TextLink href='#' onPress={handleResendCode} testID={tid('resend-code-link')}>
                {resending ? t('Sending...') : t('Resend code.')}
              </TextLink>
            </>
          )}
        </View>

        <InlineError message={error} testID={tid('error')} />

        <Input
          label={phoneHint}
          value={code}
          onChangeText={setCode}
          placeholder={t('Enter code')}
          maxLength={254}
          autoComplete='one-time-code'
          onSubmitEditing={handleSubmit}
          testID={tid('code-input')}
        />

        <View className='mt-6 gap-4'>
          <View>
            <BloomButton text={t('Submit')} onPress={handleSubmit} loading={loading} testID={tid('submit-button')} />
          </View>

          <View className='items-center justify-center mt-2'>
            <TextLink href='#' onPress={onBack} testID={tid('back-link')}>
              {t('Back')}
            </TextLink>
          </View>
        </View>
      </View>
    </View>
  );
};
